import {getCurrentUserLogin} from '@github-ui/client-env'
import {RepoPullsDashboardDeferredResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_pulls_dashboard'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQuery} from '@github-ui/react-query'
import {useLocation, useSearchParams} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useMemo} from 'react'

import {repoPullsDashboardContentRoute, repoPullsQueryResolver} from '../routes/repo-pulls-dashboard-content-route'
import {getRepoPullsAnalyticsView, getSelectedRepoPullsSidebarLink} from '../routes/repo-pulls-sidebar-links'
import {
  REPO_PULLS_DEFERRED_PERSISTER_CONFIG,
  repoPullsDeferredQueryFn,
  repoPullsToListItems,
} from '../helpers/repo-pulls-dashboard-proto'

const DEFERRED_STALE_TIME = 30_000

export function useRepoPullsSearch() {
  const location = useLocation()
  const navigate = useNavigate()
  const {owner, repo} = useRouteParams(repoPullsDashboardContentRoute)
  const [searchParams] = useSearchParams()

  const fetchedQuery = repoPullsQueryResolver({pathname: location.pathname, searchParams})
  const currentPage = Number(searchParams.get('page') || 1)
  const pathname = repoPullsDashboardContentRoute.generatePath({owner, repo})
  const currentUserLogin = getCurrentUserLogin()
  const selectedLink = getSelectedRepoPullsSidebarLink(fetchedQuery, location.pathname, currentUserLogin)
  const analyticsView = getRepoPullsAnalyticsView(fetchedQuery, location.pathname, currentUserLogin)

  function buildSearch(q: string, page = 1) {
    const nextSearchParams = new URLSearchParams()
    const normalizedQuery = q.trim()
    if (normalizedQuery) nextSearchParams.set('q', normalizedQuery)
    if (page > 1) nextSearchParams.set('page', String(page))
    const milestone = searchParams.get('milestone')
    if (milestone) nextSearchParams.set('milestone', milestone)

    return nextSearchParams.toString()
  }

  function hrefForQuery(q: string, page = 1) {
    const search = buildSearch(q, page)
    return `${pathname}${search ? `?${search}` : ''}`
  }

  function fetchResults({q, page = 1}: {q: string; page?: number}) {
    const search = buildSearch(q, page)
    navigate({pathname, search: search ? `?${search}` : ''})
    window.scrollTo({top: 0})
  }

  function getPageHref(page: number) {
    return hrefForQuery(fetchedQuery, page)
  }

  // Keep predefined filters on `?q=` so they do not collide with Rails clean paths
  // like `/:owner/:repo/pulls/:login`.
  function generateLinkPath(_path: string | undefined, query?: string) {
    return hrefForQuery(query ?? fetchedQuery)
  }

  return {
    analyticsView,
    currentPage,
    fetchedQuery,
    fetchResults,
    generateLinkPath,
    getPageHref,
    hrefForQuery,
    owner,
    repo,
    selectedLink,
  }
}

export function useRepoPullsDashboardData() {
  const mainQuery = useSuspenseRouteQuery(repoPullsDashboardContentRoute, 'mainQuery')
  const mainResults = mainQuery.data.results
  const location = useLocation()
  const {owner, repo} = useRouteParams(repoPullsDashboardContentRoute)
  const [searchParams] = useSearchParams()
  const deferredQueryDeps = useMemo(() => {
    const nextSearchParams: Array<[string, string]> = [
      ['q', repoPullsQueryResolver({pathname: location.pathname, searchParams})],
      ['page', searchParams.get('page') || '1'],
    ]
    const milestone = searchParams.get('milestone')
    if (milestone) nextSearchParams.push(['milestone', milestone])
    for (const pull of mainResults) nextSearchParams.push(['pr_ids[]', String(pull.id)])

    return {
      pathname: `/${owner}/${repo}/pulls/_deferred`,
      searchParams: nextSearchParams,
    }
  }, [location.pathname, mainResults, owner, repo, searchParams])
  const deferredQueryKey = useMemo(
    () => ['pull-requests', 'repo-pulls-dashboard', 'deferred', deferredQueryDeps],
    [deferredQueryDeps],
  )
  const deferredQuery = useQuery({
    queryKey: deferredQueryKey,
    schema: RepoPullsDashboardDeferredResponseSchema,
    queryFn: ({signal}) =>
      repoPullsDeferredQueryFn({
        routeId: 'repoPullsDashboardDeferredRoute',
        queryName: 'deferred',
        queryDeps: {...deferredQueryDeps, init: {signal}},
      }),
    enabled: mainResults.length > 0,
    staleTime: DEFERRED_STALE_TIME,
    persister: REPO_PULLS_DEFERRED_PERSISTER_CONFIG,
  })
  const deferredResults = deferredQuery.data?.error ? undefined : deferredQuery.data?.results
  const pulls = useMemo(() => repoPullsToListItems(mainResults, deferredResults), [deferredResults, mainResults])
  const stateCounts =
    mainQuery.data.openCount === undefined || mainQuery.data.closedCount === undefined
      ? undefined
      : {open: mainQuery.data.openCount, closed: mainQuery.data.closedCount}

  return {
    error: mainQuery.data.error,
    isDeferredLoading: mainResults.length > 0 && deferredQuery.isPending,
    pageInfo: {
      currentPage: mainQuery.data?.currentPage ?? 1,
      totalPages: mainQuery.data?.totalPages ?? 0,
      totalCount: mainQuery.data?.totalCount ?? 0,
    },
    pulls,
    stateCounts,
  }
}
